"use client";

import { useState, useCallback } from "react";
import { useWatermarkVersion } from "./WatermarkVersionProvider";

// Watermarking is handled server-side by /api/image-proxy (Sharp + canvas
// bakes time/device/© MEOWMI/盜用必究 into every JPEG).
// We render <img> (no canvas) so the image shows up even before React hydrates
// and with native lazy loading. Pixels carry the watermark — no client-side
// drawing required.
//
// onError retry handles transient Vercel 502s that happen when the grid fires
// 50+ lambda cold starts at once — retries with exponential backoff + a cache-
// busting query param.

const MAX_RETRIES = 2;

// Proxy outputs nominal 1000x1000 (square after 1:1 aspect wrappers).
// width/height are layout hints — eliminate CLS + let Lighthouse score
// responsive-images correctly.
const DEFAULT_W = 1000;
const DEFAULT_H = 1000;

// srcset widths served by /api/image-proxy. Must match the ALLOWED_WIDTHS
// whitelist in the proxy route.
const RESPONSIVE_WIDTHS = [400, 600, 800, 1200, 1600];

// Detect proxy-origin URLs once so we don't re-append `v=` to local assets.
function isProxySrc(base) {
  return (
    typeof base === "string" &&
    (base.includes("/api/image-proxy") ||
      base.includes("/api/admin/image-proxy"))
  );
}

// Append/override the watermark version on a proxy URL so CDN-cached JPEGs
// are invalidated the moment admins tune ratio/opacity/logo.
function applyVersion(base, version) {
  if (!base || !version || !isProxySrc(base)) return base;
  const [path, qs = ""] = base.split("?");
  const params = new URLSearchParams(qs);
  params.set("v", String(version));
  return `${path}?${params.toString()}`;
}

// Build a srcset string by appending / overriding `w=` on the base URL.
// Only applied when `responsive` is true AND the src is a proxy URL — other
// sources (local assets, blob: preview, etc.) get no srcset.
function buildSrcSet(base) {
  if (!base) return null;
  if (!isProxySrc(base)) return null;
  const u = base.split("#")[0];
  const [path, qs = ""] = u.split("?");
  const params = new URLSearchParams(qs);
  return RESPONSIVE_WIDTHS.map((w) => {
    params.set("w", String(w));
    return `${path}?${params.toString()} ${w}w`;
  }).join(", ");
}

const ProtectedImage = ({
  src,
  alt,
  width,
  height,
  priority = false,
  responsive = true,
  sizes: sizesProp,
  className = "",
  style: externalStyle,
  ...rest
}) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  const wmVersion = useWatermarkVersion();

  const handleError = useCallback(() => {
    if (attempt < MAX_RETRIES) {
      const nextAttempt = attempt + 1;
      setTimeout(() => setAttempt(nextAttempt), 600 * nextAttempt);
    } else {
      setFailed(true);
    }
  }, [attempt]);

  const versionedSrc = applyVersion(src, wmVersion);
  const actualSrc =
    attempt > 0
      ? `${versionedSrc}${versionedSrc.includes("?") ? "&" : "?"}_r=${attempt}`
      : versionedSrc;

  // srcset regenerated per attempt so cache-busting `_r` param is kept out of it.
  const srcSet = responsive ? buildSrcSet(versionedSrc) : null;
  // Default sizes: gallery is 2-col mobile / 3-col desktop → 50vw mobile, 33vw
  // desktop, capped at ~300px on wide screens. Caller can override via `sizes`.
  const sizes =
    sizesProp || "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px";

  if (failed) {
    return (
      <div
        className={className}
        style={{
          ...externalStyle,
          backgroundColor: "#f3f4f6",
          color: "#9ca3af",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
        }}
      >
        圖片載入失敗
      </div>
    );
  }

  return (
    <img
      src={actualSrc}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt || ""}
      width={width || DEFAULT_W}
      height={height || DEFAULT_H}
      className={className}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onError={handleError}
      style={{
        ...externalStyle,
        display: "block",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
      data-protected
      {...rest}
    />
  );
};

export default ProtectedImage;
